import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
} from "react"

//Referal modules
import RefGoogleAutoComplete, {
  RefGetAddressFromPlace,
} from "../Input/RefTextInputGooglePlaces"
import MapSearchRangeSlider from "./MapSearchRangeSlider"
import MapSearchSideBar from "./MapSearchSideBar"
import MapSearchMap from "./MapSearchMap"
import {
  mapBox_Places_GetCoordinates,
  getServiceCategoryNode,
  findServiceCategoryChildren,
  getBoundingBox,
  getPartnerLocationFeaturePoints,
  GetMapZoomRange,
} from "../sharedFunctions"
import RefGetErrorMessage from "../RefGetErrorMessage"

//styles
import mapSearchStyles from "../../../../styles/referrals/ReferralEntityMapSearch.module.css"
import formStyles from "../../../../styles/Form-Inputs.module.css"

//Referral API
import {
  referralsApiReferralLocationsPost,
  referralsApiReferralLocationsMappedPost,
  referralsApiReferralEntityGetCategories,
} from "../../partnerAPI"

// Context
import { UserContext } from "../../../../context/userContext"
import RefHelp from "./RefHelp"

//Primereact
import { Dropdown } from "primereact/dropdown"
import { InputText } from "primereact/inputtext"
import { Button } from "primereact/button"
import { Badge } from "primereact/badge"
import { Dialog } from "primereact/dialog"
import MapSearchSideBarCatFilter from "./MapSearchSideBarCatFilter"
import { LogError } from "../../../../helpers/logger"
import { Tooltip } from "primereact/tooltip"
import { LoaderMedium } from "../../../../components/Loaders"

export default function MapSearch({
  initialLong,
  initialLat,
  address,
  referralLocations,
  referralMapLocations,
  referralCategories,
  cachedMap,
  mapSearch,
  filterCategory,
  onSetReferralLocations,
  onSetReferralMapLocations,
  onSetReferralCategories,
  onSetMapCache,
  onEntitySelectedCallBack,
  onSearchOptionChangeCallBack,
  isPopup,
}) {
  const userCtx = useContext(UserContext)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingLocations, setIsLoadingLocations] = useState(true)
  const [isLoadingMapLocations, setIsLoadingMapLocations] = useState(true)
  const [points, setPoints] = useState()
  const [centerLocation, setCenterLocation] = useState({
    longitude: -77,
    latitude: 38,
  })
  const [centerPoint, setCenterPoint] = useState({
    longitude: -77,
    latitude: 38,
  })
  const [triggerMapRefocus, setTriggerMapRefocus] = useState(false)

  const [fitToBounds, setFitToBound] = useState()
  const [showPopupEntity, setShowPopupEntity] = useState()
  const [entitySearchText, setEntitySearchText] = useState("")
  const [categoryFilter, setCategoryFilter] = useState([])
  const [showCategoryDialog, setShowCategoryDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")

  const [loadListOnly, setLoadListOnly] = useState(false)
  const [activeFilters, setActiveFilters] = useState({
    searchPhrase: entitySearchText,
    allLocations: false,
    distance: 20,
    centerLongitude: centerPoint.longitude,
    centerLatitude: centerPoint.latitude,
    referralCategories: [],
    pageSize: 25,
    pageNum: 1,
    viewOption: "List",
  })

  const filteredEntitiesRef = useRef()
  filteredEntitiesRef.current = referralLocations

  const viewOptionLayout = {
    List: { containerClassName: mapSearchStyles.mapSearchContainerList },
    Map: { containerClassName: mapSearchStyles.mapSearchContainer },
    Both: { containerClassName: mapSearchStyles.mapSearchContainer },
  }

  useEffect(() => {
    if (
      filterCategory &&
      filterCategory !== "" &&
      !!referralCategories &&
      referralCategories.length > 0
    ) {
      referralCategories[0].children.some((cat) => {
        if (cat.label === filterCategory) {
          const allCatNodes = findServiceCategoryChildren(cat)
          setCategoryFilter(allCatNodes)
          return true
        } else {
          return false
        }
      })
    }
  }, [filterCategory, referralCategories])

  const getReferralLocationList = useCallback(() => {
    //Load list data
    if (
      activeFilters.viewOption === "List" ||
      activeFilters.viewOption === "Both"
    ) {
      let filter = {
        ...activeFilters,
        organizationId: userCtx.session.user.organization_id,
      }

      if (activeFilters.viewOption === "List") {
        filter.allLocations = true
        filter.pageSize = 25
      }

      setIsLoadingLocations(true)
      referralsApiReferralLocationsPost(userCtx.token, filter)
        .then((result) => {
          onSetReferralLocations(result)
          setIsLoadingLocations(false)

          if (!filter.allLocations) {
            if (result?.locations?.length === 0) {
              userCtx.setNotificationError(
                "There are no partners available within the specified range"
              )
            }
          }
        })
        .catch((error) => {
          LogError("Unable to load referral location list", error)
          userCtx.setNotificationError("Unable to load referral location list")
          onSetReferralLocations([])
          setIsLoadingLocations(false)
        })
    }

    //Load map data
    if (
      !loadListOnly &&
      (activeFilters.viewOption === "Map" ||
        activeFilters.viewOption === "Both")
    ) {
      setIsLoadingMapLocations(true)
      referralsApiReferralLocationsMappedPost(userCtx.token, {
        ...activeFilters,
        organizationId: userCtx.session.user.organization_id,
      })
        .then((result) => {
          onSetReferralMapLocations(result)

          if (centerPoint && result?.locations?.length > 0) {
            const ClosestDistance = Math.min(
              ...result?.locations.map((location) => location.distance)
            )
            const ClosestLocations = result.locations.filter(
              (location) => location.distance === ClosestDistance
            )

            const boundingBox = getBoundingBox(ClosestLocations[0], centerPoint)
            setFitToBound({
              mappedReferralEntity: ClosestLocations[0],
              boundsBox: boundingBox,
            })
          }
          setIsLoadingMapLocations(false)
        })
        .catch((error) => {
          LogError("Unable to load mapped referral locations", error)
          userCtx.setNotificationError(
            "Unable to load mapped referral locations"
          )
          onSetReferralMapLocations([])
          setIsLoadingMapLocations(false)
        })
    }

    if (loadListOnly) {
      setLoadListOnly(false)
    }

    setIsLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilters])

  useEffect(() => {
    getReferralLocationList()
  }, [getReferralLocationList])

  useEffect(() => {
    if (initialLat && initialLong) {
      setCenterLocation({ longitude: initialLong, latitude: initialLat })
      setCenterPoint({ longitude: initialLong, latitude: initialLat })
    } else if (address) {
      mapBox_Places_GetCoordinates(address, userCtx.mapBoxKey)?.then((location) => {
        if (location) {
          setCenterLocation(location)
          setCenterPoint(location)
          setActiveFilters({
            ...activeFilters,
            centerLongitude: location.longitude,
            centerLatitude: location.latitude,
            pageNum: 1,
          })
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, address.initialLat, initialLat, initialLong])

  useEffect(() => {
    if (!referralCategories || referralCategories.length === 0) {
      referralsApiReferralEntityGetCategories(userCtx.token)
        .then((data) => {
          data.unshift({
            referralCategory: { id: -1, name: "<Uncategorized>" },
            serviceCategories: [],
          }) //Add new element to front of array

          const allCategories = [
            {
              referralCategory: { id: -2, name: "[All categories]" },
              serviceCategories: data,
            },
          ]

          let nodes = allCategories.map((cat) => getServiceCategoryNode(cat))

          onSetReferralCategories(nodes)
        })
        .catch((error) => {
          console.log(error)
          setErrorMessage(RefGetErrorMessage(error))
        })
    } else {
    }
  }, [onSetReferralCategories, referralCategories, userCtx.token])

  useEffect(() => {
    const points = getPartnerLocationFeaturePoints(
      referralMapLocations,
      activeFilters.distance
    )

    setPoints(points)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralMapLocations])

  //Allow user to move map manually:
  const onMove = (viewState) => {
    setCenterLocation({
      longitude: viewState.viewState.longitude,
      latitude: viewState.viewState.latitude,
    })
    return ""
  }

  const locateReferralEntity = (referralLocationId) => {
    const selectedRefEntity = referralLocations?.locations?.filter((x) => {
      return x.locationId === referralLocationId
    })
    if (selectedRefEntity.length > 0) {
      setCenterLocation({
        longitude: selectedRefEntity[0].longitude,
        latitude: selectedRefEntity[0].latitude,
      })
      setShowPopupEntity(selectedRefEntity[0])
    }

    return ""
  }

  //When the place changes, change the center of the map
  function onPlaceChanged(place) {
    const placeDetail = RefGetAddressFromPlace(place)

    if (placeDetail.longitude && placeDetail.latitude) {
      setCenterLocation({
        longitude: placeDetail.longitude,
        latitude: placeDetail.latitude,
      })
      setCenterPoint({
        longitude: placeDetail.longitude,
        latitude: placeDetail.latitude,
      })
      setActiveFilters({
        ...activeFilters,
        centerLongitude: placeDetail.longitude,
        centerLatitude: placeDetail.latitude,
        pageNum: 1,
      })
    }

    return ""
  }

  const onMouseWheelZoom = (event) => {
    const zoom = event.target.getZoom()

    const zoomRange = GetMapZoomRange(zoom)

    if (
      !activeFilters.allLocations &&
      (activeFilters.distance < zoomRange.minRange ||
        activeFilters.distance > zoomRange.maxRange)
    ) {
      setActiveFilters({ ...activeFilters, distance: zoomRange.range })
    }
  }

  function onRefocusClicked() {
    //Toggle refocus to refocus on current location
    setTriggerMapRefocus(!triggerMapRefocus)
  }

  const onReferralLocationSelected = (locationId) => {
    const location = filteredEntitiesRef.current?.locations?.filter((x) => {
      return x.locationId === locationId
    })
    if (location) {
      onEntitySelectedCallBack(location[0])
    }
    return ""
  }

  const onNewReferralClick = () => {
    onSearchOptionChangeCallBack()
  }

  return (
    <>
      <Dialog
        header="Category filter"
        onHide={() => {
          setShowCategoryDialog(false)

          let catFilter = []
          if (
            categoryFilter?.length > 0 &&
            !categoryFilter?.some((x) => x.label === "[All categories]")
          ) {
            catFilter = categoryFilter
              .filter((x) => !x.children)
              .map((x) => x.key)
          }

          setActiveFilters({
            ...activeFilters,
            referralCategories: catFilter,
            pageNum: 1,
          })
        }}
        visible={showCategoryDialog}
        baseZIndex={1000}
      >
        <MapSearchSideBarCatFilter
          categoryList={referralCategories}
          selectedCategories={categoryFilter}
          onSelectedCategoriesChanged={setCategoryFilter}
        />
      </Dialog>
      {!isPopup ? <h2> Referral entity search </h2> : null}
      {errorMessage !== "" ? (
        <div style={{ marginBottom: "10px" }}>
          <label className={formStyles.errFieldMsg}>{errorMessage}</label>
        </div>
      ) : null}
      <div className={mapSearchStyles.viewOptionsContainer}>
        <Tooltip target=".filter-header-button" />
        <div className={mapSearchStyles.viewOptionGroup}>
          <button
            type="button"
            className={`btn ${
              mapSearchStyles.viewOptionListView
            } filter-header-button ${mapSearchStyles.viewOptionButton} ${
              activeFilters.viewOption === "List"
                ? mapSearchStyles.viewOptionSelectedView
                : ""
            }`}
            onClick={(e) => {
              e.stopPropagation()
              setActiveFilters({
                ...activeFilters,
                viewOption: "List",
                pageNum: 1,
              })
            }}
            data-pr-tooltip="Show partners in list view"
            data-pr-position="bottom"
            disabled={isLoading}
          >
            <span className={`material-icons`}> {"menu"} </span>
          </button>
          <button
            type="button"
            className={`btn ${
              mapSearchStyles.viewOptionListMapView
            } filter-header-button ${mapSearchStyles.viewOptionButton} ${
              activeFilters.viewOption === "Both"
                ? mapSearchStyles.viewOptionSelectedView
                : ""
            }`}
            onClick={(e) => {
              e.stopPropagation()
              setActiveFilters({
                ...activeFilters,
                viewOption: "Both",
                pageNum: 1,
              })
            }}
            data-pr-tooltip="Show partners in a list and on a map"
            data-pr-position="bottom"
            disabled={isLoading}
          >
            <span className={`material-icons`}> {"vertical_split"} </span>
          </button>
          <button
            type="button"
            className={`btn ${
              mapSearchStyles.viewOptionMapView
            } filter-header-button ${mapSearchStyles.viewOptionButton} ${
              activeFilters.viewOption === "Map"
                ? mapSearchStyles.viewOptionSelectedView
                : ""
            }`}
            onClick={(e) => {
              e.stopPropagation()
              setActiveFilters({
                ...activeFilters,
                viewOption: "Map",
                pageNum: 1,
              })
            }}
            data-pr-tooltip="Show partners on a map"
            data-pr-position="bottom"
            disabled={isLoading}
          >
            <span className={`material-icons`}> {"language"} </span>
          </button>
        </div>
      </div>

      <div
        className={
          viewOptionLayout[activeFilters.viewOption].containerClassName
        }
      >
        <div
          className={`${mapSearchStyles.mapSearchItemSearchTitle} ${mapSearchStyles.mapSearchTitleItem}`}
        >
          Partner search
        </div>
        <div className={mapSearchStyles.mapSearchItemSearch}>
          <span
            className="p-input-icon-right"
            style={{ flexGrow: "2", border: "none" }}
          >
            <i className="pi pi-search " />
            <InputText
              value={entitySearchText}
              placeholder="Partner Search"
              style={{ width: "100%", marginTop: "5px" }}
              disabled={isLoading}
              onChange={(e) => {
                setEntitySearchText(e.target.value)
              }}
              onKeyDown={(e) => {
                if (e.keyCode === 13 || e.key === "Enter") {
                  setActiveFilters({
                    ...activeFilters,
                    searchPhrase: entitySearchText,
                    pageNum: 1,
                  })
                }
              }}
            />
          </span>

          {entitySearchText !== activeFilters.searchPhrase ? (
            <Button
              label="Search"
              className={`${mapSearchStyles.button} ${mapSearchStyles.mapSearchItemSearchButton}`}
              onClick={() => {
                setActiveFilters({
                  ...activeFilters,
                  searchPhrase: entitySearchText,
                  pageNum: 1,
                })
              }}
            />
          ) : null}
        </div>
        {activeFilters.viewOption !== "List" ? (
          <>
            <div
              className={`${mapSearchStyles.mapSearchItemDistanceTitle} ${mapSearchStyles.mapSearchTitleItem} ${mapSearchStyles.mapSearchTitleItemRightAlign}`}
            >
              <RefHelp
                content={
                  <p>
                    If there are referral partners in range, mapped and
                    categorized partners will be displayed by default (The in
                    range option).
                    <br />
                    Click on All to see referral partners regardless of
                    distance.
                  </p>
                }
              />
              Distance filter
            </div>
            <div className={mapSearchStyles.mapSearchItemDistance}>
              <Dropdown
                className={mapSearchStyles.mapSearchItemDistanceInput}
                value={activeFilters.allLocations}
                options={[
                  { label: "In Range", value: false },
                  { label: "All", value: true },
                ]}
                onChange={(e) => {
                  setActiveFilters({
                    ...activeFilters,
                    allLocations: e.value,
                    pageNum: 1,
                  })
                }}
                disabled={isLoading}
              />
            </div>
          </>
        ) : null}

        <div
          className={`${mapSearchStyles.mapSearchItemCategoryTitle} ${mapSearchStyles.mapSearchTitleItem} ${mapSearchStyles.mapSearchTitleItemRightAlign}`}
        >
          <RefHelp
            content={
              <p>
                Click on the Filter button to select referral categories to
                filter partners by.
              </p>
            }
          />
          Category filters
        </div>
        <div className={mapSearchStyles.mapSearchItemCategory}>
          <div className="p-overlay-badge">
            <Button
              label="Filter"
              onClick={() => {
                setShowCategoryDialog(true)
              }}
              className={`${mapSearchStyles.button} ${mapSearchStyles.mapSearchItemCategoryInput}`}
              loading={!referralCategories}
              disabled={!referralCategories || referralCategories.length === 0}
            />
            {categoryFilter && categoryFilter.length > 0 && (
              <Badge
                value={categoryFilter.length}
                style={{
                  fontSize: 12,
                  transform: "translate(0%,-40%)",
                  background: "orange",
                }}
              />
            )}
          </div>
        </div>
        {!activeFilters?.allLocations && activeFilters.viewOption !== "List" ? (
          <>
            <div className={mapSearchStyles.mapSearchItemLocation}>
              <RefGoogleAutoComplete
                label="Map location"
                showHelpText={true}
                onPlaceSelect={onPlaceChanged}
                disabled={isLoading}
                defaultValue={address}
                overrideInputWidth="100%"
                overrideLabelWidth="110px"
                divStyle={{
                  width: "100%",
                  display: "inline-flex",
                  alignItems: "center",
                }}
                onRefocusClicked={onRefocusClicked}
              />
            </div>
            <div
              className={`${mapSearchStyles.mapSearchItemRangeTitle} ${mapSearchStyles.mapSearchTitleItem} ${mapSearchStyles.mapSearchTitleItemRightAlign}`}
            >
              <RefHelp content="The range indicator adjusts the acceptable distance from the participant address to the referral partner location in miles. " />
              Range
            </div>
            <div className={mapSearchStyles.mapSearchItemRange}>
              <MapSearchRangeSlider
                rangeValue={activeFilters.distance}
                onChange={(e) => {
                  setActiveFilters({
                    ...activeFilters,
                    distance: e,
                    pageNum: 1,
                  })
                  return ""
                }}
                isLoading={isLoading}
              />
            </div>
          </>
        ) : null}
      </div>

      <div
        className={`${mapSearchStyles.mainMapContainer} ${
          activeFilters.allLocations || activeFilters.viewOption === "List"
            ? ""
            : mapSearchStyles.mainMapContainerSmall
        }`}
      >
        {activeFilters.viewOption !== "Map" ? (
          <div
            className={
              activeFilters.viewOption === "List"
                ? mapSearchStyles.refEntityMapSearchList
                : mapSearchStyles.refEntityMapSearch
            }
          >
            <MapSearchSideBar
              referralLocations={referralLocations}
              categoryFilter={categoryFilter}
              onLocate={locateReferralEntity}
              onSelect={onReferralLocationSelected}
              onNewReferralClick={onNewReferralClick}
              searchText={entitySearchText}
              isLoadingLocations={isLoadingLocations}
              showAllEntities={activeFilters.allLocations}
              onChangeLocationPage={(newPage) => {
                setLoadListOnly(true)
                setActiveFilters({ ...activeFilters, pageNum: newPage })
              }}
              ignoreDistance={activeFilters.viewOption === "List"}
              maxDistance={activeFilters.distance}
            />
          </div>
        ) : null}

        {activeFilters.viewOption !== "List" ? (
          <div
            id="map"
            className={
              activeFilters.viewOption === "Map"
                ? mapSearchStyles.mapOnly
                : mapSearchStyles.map
            }
          >
            {isLoadingMapLocations ? (
              <div className={mapSearchStyles.mapLoader}>
                <LoaderMedium />
              </div>
            ) : null}
            <MapSearchMap
              centerLocation={centerLocation}
              focusLocation={centerPoint}
              defaultZoom={10}
              fitToBounds={fitToBounds}
              thisMap={cachedMap}
              onMove={onMove}
              onLocationSelect={onReferralLocationSelected}
              setThisMap={(map) => {
                onSetMapCache(map)
              }}
              markerPoints={points}
              triggerResize={activeFilters.viewOption}
              refEntityShowPopup={showPopupEntity}
              triggerRefocus={triggerMapRefocus}
              showAllEntities={activeFilters.allLocations}
              onWheelZoom={onMouseWheelZoom}
            />
          </div>
        ) : null}
      </div>
    </>
  )
}
